import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/dist/query';
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { ReducerPaths } from '../../constants/reducerPaths';

export type CandidateAiDataBuilder = EndpointBuilder<
  BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    {},
    FetchBaseQueryMeta
  >,
  'aiData' | 'aiScore',
  ReducerPaths.CANDIDATE_AI_DATA
>;

export type GenerateAiDataInput = {
  candidateIds: number[];
};

export type GetCandidateAiDataInput = {
  candidateId: number;
  getOverride?: boolean;
};

export type GetCandidateAiDataOutput = {
  english_language_proficiency: {
    cefr_score: string;
    clarity: string;
    accent: string;
    simera_score: number;
    fluency: string;
    vocabulary_range: string;
  };
  voice: {
    tones: string[];
    volume: string;
    pace: string;
  };
  facial_expression: {
    expressions: string[];
    consistency: string;
    relevance_to_content: string;
  };
  emotion_detection: {
    emotions: string[];
    intensity: {
      happy: string;
      confident: string;
      focused: string;
      excited: string;
      curious: string;
      anxious: string;
    };
  };
  posture_and_gesture: {
    body_movements: string;
    postures: string;
    eye_contact: string;
    gestures: string;
  };
  behavioral_patterns: {
    patterns: string[];
    consistency: string;
    situational_appropriateness: string;
  };
  personality_traits: {
    traits: string[];
    dominant_trait: string;
  };
  jump_around: {
    average_years_per_job: number;
    total_jobs: number;
    score: number;
    recent_trend: string;
  };
  job_fits: [
    {
      name: string;
      reason: string;
      match_score: number;
      industry_experience: string;
      salary_min: number;
      salary_max: number;
    },
    {
      name: string;
      reason: string;
      match_score: number;
      industry_experience: string;
      salary_min: number;
      salary_max: number;
    },
    {
      name: string;
      reason: string;
      match_score: number;
      client_handling_experience: string;
      salary_min: number;
      salary_max: number;
    },
  ];
  experience_levels: [
    {
      job_name: string;
      years_of_experience: number;
      skill_relevance: string;
    },
    {
      job_name: string;
      years_of_experience: number;
      skill_relevance: string;
    },
    {
      job_name: string;
      years_of_experience: number;
      skill_relevance: string;
    },
    {
      job_name: string;
      years_of_experience: number;
      skill_relevance: string;
    },
    {
      job_name: string;
      years_of_experience: number;
      skill_relevance: string;
    },
    {
      job_name: string;
      years_of_experience: number;
      skill_relevance: string;
    },
    {
      job_name: string;
      years_of_experience: number;
      skill_relevance: string;
    },
    {
      job_name: string;
      years_of_experience: number;
      skill_relevance: string;
    },
    {
      job_name: string;
      years_of_experience: number;
      skill_relevance: string;
    },
  ];
  professionalism: {
    curriculum_presentation: number;
    video_presentation: number;
    overall: number;
    attire: string;
  };
  soft_skills: {
    communication: number;
    leadership: number;
    adaptability: number;
    critical_thinking: number;
    teamwork: number;
    problem_solving: number;
    organization: number;
    creativity: number;
    persuasion: number;
    open_to_criticism: number;
    time_management: number;
    empathy: number;
    conflict_resolution: number;
  };
  sentiment: {
    positive: number;
    neutral: number;
    negative: number;
    overall_sentiment: string;
  };
  keywords: [
    {
      word: string;
      count: number;
      context: string;
    },
  ];
  overrides?: {
    simera_english?: number;
    professional_presence?: number;
    drive_initiative?: number;
    timeliness?: CandidateTimelinessScore;
  };
};

export enum CandidateTimelinessScore {
  Exceptional = 'Exceptional',
  'Above Average' = 'Above Average',
  Average = 'Average',
  'Below Average' = 'Below Average',
  'Needs Improvement' = 'Needs Improvement',
}

export type OverrideAiScoresInput = {
  candidateId: number;
  payload: {
    'soft_skills.communication'?: number;
    'soft_skills.adaptability'?: number;
    'soft_skills.problem_solving'?: number;
    'jump_around.score'?: number;
    'overrides.simera_english'?: number;
    'overrides.timeliness'?: CandidateTimelinessScore;
    'overrides.professional_presence'?: number;
    'overrides.drive_initiative'?: number;
  };
};

type CandidateAIScoreConfig =
  | {
      options: string[];
      numericMapping: Record<string, number>;
    }
  | { max: number; min: number; step: number };

export enum CandidateAIScoreType {
  numeric = 'Numeric',
  select = 'Select',
}

export type CandidateAIScoreOutput = {
  Id: number;
  SystemValue: number | null;
  SystemScore: number | null;
  OverrideValue: number;
  OverrideScore: number;
  FinalValue: number;
  FinalScore: number;
  IsOverridden: boolean;
  HasSystemScore: boolean;
  Source: string;
  CandidateScoreType: {
    DisplayOrder: number;
    ScoreType: CandidateAIScoreType;
    Id: number;
    Name: string;
    Slug: string;
    ScoreGroup: string;
    Config: CandidateAIScoreConfig;
  };
}[];

export type OverrideAiScoresBySlugInput = {
  ScoreType: string;
  Value: number;
  candidateId: number;
};

export type GetCandidateScoreSlugsOutput = {
  DisplayOrder: number;
  ScoreType: CandidateAIScoreType;
  Id: number;
  Name: string;
  Slug: string;
  ScoreGroup: string;
  Config: CandidateAIScoreConfig;
}[];

export type GetCandidateAiScoreOutput = {
  Id: number;
  SystemValue: null | string | number;
  SystemScore: null | number;
  OverrideValue: number;
  OverrideScore: number;
  FinalValue: number | string;
  FinalScore: number;
  IsOverridden: boolean;
  HasSystemScore: boolean;
  Source: string;
  DisplayOrder: number;
  ScoreType: CandidateAIScoreType;
  Name: string;
  Slug: string;
  ScoreGroup: string;
  Config: CandidateAIScoreConfig;
  OldAiData?: boolean;
}[];
